const DOMAIN_LEXICON = {
  excelente: 2.4,
  sobresaliente: 2.2,
  eficiente: 1.9,
  oportuno: 1.7,
  oportuna: 1.7,
  rapido: 1.6,
  rapida: 1.6,
  agil: 1.6,
  claro: 1.4,
  clara: 1.4,
  transparente: 1.5,
  cumplido: 1.5,
  cumple: 1.4,
  confiable: 1.7,
  estable: 1.1,
  mejora: 1.2,
  mejoro: 1.3,
  resolvio: 1.6,
  solucion: 1.2,
  disponibilidad: 0.4,
  satisfecho: 1.7,
  satisfactoria: 1.6,
  positivo: 1.4,
  buena: 1.2,
  bueno: 1.2,

  deficiente: -2.1,
  pesimo: -2.4,
  pesima: -2.4,
  incumplimiento: -2.2,
  incumple: -2.0,
  retraso: -1.9,
  retrasos: -1.9,
  demora: -1.8,
  demoras: -1.8,
  tarde: -1.3,
  caro: -1.4,
  cara: -1.4,
  sobrecosto: -1.8,
  sobrecostos: -1.8,
  cobro: -1.3,
  cobros: -1.3,
  inesperado: -1.2,
  inesperados: -1.2,
  problema: -1.5,
  problemas: -1.5,
  queja: -1.8,
  quejas: -1.8,
  reclamo: -1.5,
  reclamos: -1.5,
  riesgo: -1.4,
  critico: -2.0,
  critica: -2.0,
  colapso: -2.3,
  congestion: -1.6,
  congestiones: -1.6,
  falta: -1.5,
  escasez: -1.8,
  dano: -1.5,
  danos: -1.5,
  danado: -1.5,
  danados: -1.5,
  error: -1.4,
  errores: -1.4,
  mala: -1.6,
  malo: -1.6,
  negativo: -1.5,
  roll: -1.4,
  rollover: -1.8,
  blank: -1.6,
};

const PHRASE_LEXICON = [
  { pattern: /\bsin problemas relevantes\b/i, score: 2.0 },
  { pattern: /\bsin problema(s)?\b/i, score: 1.7 },
  { pattern: /\bsin retraso(s)?\b/i, score: 1.6 },
  { pattern: /\ba tiempo\b/i, score: 1.5 },
  { pattern: /\bbuena comunicacion\b/i, score: 1.6 },
  { pattern: /\bcomunicacion proactiva\b/i, score: 1.7 },
  { pattern: /\bmal servicio\b/i, score: -2.1 },
  { pattern: /\bmala comunicacion\b/i, score: -2.0 },
  { pattern: /\bfalta de espacio\b/i, score: -1.9 },
  { pattern: /\bfalta de contenedores\b/i, score: -2.0 },
  { pattern: /\bcobros inesperados\b/i, score: -2.0 },
  { pattern: /\bcambios de eta\b/i, score: -1.5 },
  { pattern: /\bblank sailing\b/i, score: -1.7 },
  { pattern: /\broll[- ]?over\b/i, score: -1.8 },
  { pattern: /\bno resuelto\b/i, score: -2.2 },
  { pattern: /\bno respond(e|en|ieron)\b/i, score: -1.9 },
];

const NEGATORS = new Set(['no', 'sin', 'nunca', 'jamas', 'tampoco', 'ningun', 'ninguna']);
const INTENSIFIERS = {
  muy: 1.35,
  bastante: 1.2,
  demasiado: 1.35,
  totalmente: 1.25,
  poco: 0.7,
  parcialmente: 0.75,
};

const MIN_MEANINGFUL_WORDS = 3;
const SENTIMENT_THRESHOLD = 0.18;
const Z_95 = 1.96;

const normalize = (text = '') => text
  .toString()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const tokenize = (text) => normalize(text)
  .replace(/[^a-z0-9%+.-]+/g, ' ')
  .split(/\s+/)
  .filter(Boolean);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const wilsonInterval = (successes, total) => {
  if (!total) return { low: 0, high: 0 };
  const phat = successes / total;
  const z2 = Z_95 ** 2;
  const denominator = 1 + z2 / total;
  const center = (phat + z2 / (2 * total)) / denominator;
  const margin = (Z_95 * Math.sqrt((phat * (1 - phat) + z2 / (4 * total)) / total)) / denominator;
  return {
    low: Math.round(clamp((center - margin) * 100, 0, 100)),
    high: Math.round(clamp((center + margin) * 100, 0, 100)),
  };
};

export const analyzeSentimentDetail = (text) => {
  const normalized = normalize(text);
  const tokens = tokenize(text);

  if (tokens.length < MIN_MEANINGFUL_WORDS) {
    return {
      sentiment: 'neutral',
      score: 0,
      confidence: 0,
      positiveHits: 0,
      negativeHits: 0,
      matchedTerms: [],
    };
  }

  let rawScore = 0;
  const matchedTerms = [];

  PHRASE_LEXICON.forEach((entry) => {
    if (entry.pattern.test(normalized)) {
      rawScore += entry.score;
      matchedTerms.push({ term: entry.pattern.source, score: entry.score, type: 'phrase' });
    }
  });

  tokens.forEach((token, index) => {
    const baseScore = DOMAIN_LEXICON[token];
    if (!baseScore) return;

    const previousTokens = tokens.slice(Math.max(0, index - 3), index);
    const hasNegator = previousTokens.some((item) => NEGATORS.has(item));
    const intensity = previousTokens.reduce((factor, item) => factor * (INTENSIFIERS[item] || 1), 1);
    const score = baseScore * intensity * (hasNegator ? -0.85 : 1);

    rawScore += score;
    matchedTerms.push({ term: token, score, type: hasNegator ? 'negated_token' : 'token' });
  });

  const denominator = Math.sqrt(tokens.length) + 1.5;
  const normalizedScore = clamp(rawScore / denominator, -1, 1);
  const confidence = clamp(Math.abs(normalizedScore), 0, 1);
  const sentiment = normalizedScore > SENTIMENT_THRESHOLD
    ? 'positive'
    : normalizedScore < -SENTIMENT_THRESHOLD
      ? 'negative'
      : 'neutral';

  return {
    sentiment,
    score: Number(normalizedScore.toFixed(3)),
    confidence: Number(confidence.toFixed(3)),
    positiveHits: matchedTerms.filter((item) => item.score > 0).length,
    negativeHits: matchedTerms.filter((item) => item.score < 0).length,
    matchedTerms,
  };
};

export const analyzeSentiment = (text) => analyzeSentimentDetail(text).sentiment;

const collectTextValues = (value) => {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(collectTextValues);
  if (value && typeof value === 'object') return Object.values(value).flatMap(collectTextValues);
  return [];
};

export const summarizeSentiments = (submissions) => {
  const buckets = {
    positive: [],
    negative: [],
    neutral: [],
  };

  const analyzed = [];

  submissions.forEach((submission) => {
    collectTextValues(submission.data).forEach((value) => {
      const text = value.toString().trim();
      if (text.length < 12) return;

      const detail = analyzeSentimentDetail(text);
      const item = {
        text,
        score: detail.score,
        confidence: detail.confidence,
        sentiment: detail.sentiment,
      };

      analyzed.push(item);
      buckets[detail.sentiment].push(item);
    });
  });

  const total = analyzed.length;
  const positiveCount = buckets.positive.length;
  const negativeCount = buckets.negative.length;
  const neutralCount = buckets.neutral.length;
  const averageScore = total
    ? analyzed.reduce((sum, item) => sum + item.score, 0) / total
    : 0;
  const averageConfidence = total
    ? analyzed.reduce((sum, item) => sum + item.confidence, 0) / total
    : 0;
  const marginOfError = total
    ? Math.round((Z_95 * Math.sqrt(0.25 / total)) * 100)
    : 0;
  const confidenceLabel = total >= 30
    ? 'base robusta'
    : total >= 12
      ? 'base direccional'
      : total > 0
        ? 'muestra exploratoria'
        : 'sin muestra';

  return {
    samples: {
      positive: buckets.positive.slice(0, 5).map((item) => item.text),
      negative: buckets.negative.slice(0, 5).map((item) => item.text),
      neutral: buckets.neutral.slice(0, 5).map((item) => item.text),
    },
    positivePct: total ? Math.round((positiveCount / total) * 100) : 0,
    negativePct: total ? Math.round((negativeCount / total) * 100) : 0,
    neutralPct: total ? Math.round((neutralCount / total) * 100) : 0,
    counts: {
      positive: positiveCount,
      negative: negativeCount,
      neutral: neutralCount,
    },
    sampleCount: total,
    averageScore: Number(averageScore.toFixed(3)),
    averageConfidence: Number(averageConfidence.toFixed(3)),
    netScore: Math.round(((averageScore + 1) / 2) * 100),
    marginOfError,
    confidenceLabel,
    intervals: {
      positive: wilsonInterval(positiveCount, total),
      negative: wilsonInterval(negativeCount, total),
      neutral: wilsonInterval(neutralCount, total),
    },
  };
};
